import { Region } from '@heidi/schema/ml-scribe';
import { useEffect, useMemo, useState } from 'react';
import type { SourceItem } from '../../../contracts';
import { buildBlockedHostSet, isBlockedHost, isRegionMatch } from '../../../contracts';
import { getRegion } from '../../../data/injected';
import { useBlockedDomains } from '../../../data/query/useBlockedDomains';
import { useAllSources } from './hooks/useAllSources';

const REGION_MAPPING: Record<string, string[]> = {
  [Region.AU]: ['Australia'],
  [Region.US]: ['United States'],
  [Region.UK]: ['United Kingdom'],
  [Region.CA]: ['Canada'],
  [Region.EU]: ['European Union', 'Europe'],
};

function getRelevanceTier(source: SourceItem, lowerQuery: string): number {
  if (source.labelLower === lowerQuery || source.domainLower === lowerQuery) return 0;
  if (
    source.labelLower.startsWith(lowerQuery) ||
    source.domainLower.startsWith(lowerQuery)
  ) {
    return 1;
  }
  return 2;
}

const MIN_SCORE = 65;
const MAX_RESULTS = 20;

/**
 * Minimum query length before suggestions render.
 *
 * Without this gate, an empty query exposes the full multi-thousand
 * trusted-source catalog ordered by authority, which mixes specialties
 * and reads as noise next to the user's curated collection list (see
 * GRO follow-up on the recommendation Customise flow). Three characters
 * is short enough to surface intent (`bmj`, `cdc`, `racgp`) without
 * forcing the user to type an entire word.
 */
export const MIN_QUERY_LENGTH = 3;

interface UseFilteredSourcesOptions {
  maxResults?: number;
}

/** Shared hook for filtering, sorting, and ranking sources by query + region. */
export function useFilteredSources(
  query: string,
  excludedDomains: string[],
  options: UseFilteredSourcesOptions = {}
) {
  const { allSources } = useAllSources(MIN_SCORE);
  const excludedSet = useMemo(() => new Set(excludedDomains), [excludedDomains]);

  // Content-safety denylist. The catalog is already filtered server-side
  // (`get_sources` hides denied domains), so this is defensive belt-and-braces
  // that also guarantees every picker built on this hook stays consistent with
  // the inline custom-domain guard. Normalized once per (near-static) list.
  const { data: blockedDomains } = useBlockedDomains();
  const blockedHosts = useMemo(
    () => buildBlockedHostSet(blockedDomains ?? []),
    [blockedDomains]
  );

  const userRegions = useMemo(() => {
    const region = getRegion();
    return REGION_MAPPING[region] ?? ['Global'];
  }, []);

  const filteredResults = useMemo(() => {
    const lowerQuery = query.trim().toLowerCase();

    // Suppress the catalog browse until the user has expressed intent.
    // Below the threshold there is no signal to rank against, so any
    // ordering (alphabetical, by authority, by region) ends up looking
    // arbitrary to the user — they expect "what I'm searching for", not
    // "highest-authority sources we know about".
    if (lowerQuery.length < MIN_QUERY_LENGTH) {
      return [];
    }

    const filtered = allSources.filter(
      (s) =>
        !excludedSet.has(s.domain) &&
        !isBlockedHost(s.domain, blockedHosts) &&
        (s.labelLower.includes(lowerQuery) || s.domainLower.includes(lowerQuery))
    );

    filtered.sort((a, b) => {
      const tierDiff = getRelevanceTier(a, lowerQuery) - getRelevanceTier(b, lowerQuery);
      if (tierDiff !== 0) return tierDiff;
      const aRegion = isRegionMatch(a, userRegions) ? 0 : 1;
      const bRegion = isRegionMatch(b, userRegions) ? 0 : 1;
      if (aRegion !== bRegion) return aRegion - bRegion;
      return b.authoritativenessScore - a.authoritativenessScore;
    });

    return options.maxResults ? filtered.slice(0, options.maxResults) : filtered;
  }, [query, allSources, excludedSet, blockedHosts, userRegions, options.maxResults]);

  return { filteredResults, allSources, excludedSet };
}

export function useSourceSearch(excludedDomains: string[]) {
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const { filteredResults, allSources, excludedSet } = useFilteredSources(
    query,
    excludedDomains,
    { maxResults: MAX_RESULTS }
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredResults]);

  return {
    query,
    setQuery,
    highlightedIndex,
    setHighlightedIndex,
    filteredResults,
    allSources,
    excludedSet,
  };
}
